import { Button, Icon, IconButton } from '@hevoio/hevo-ui-toolkit';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { GenericPageShimmer } from '../../../loader/components/generic-page-shimmer/GenericPageShimmer';
import HdButton from '../../../button/components/button/HdButton';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useEntityLogo from '../../../media/hooks/useEntityLogo';
import { useQuery } from '../../../core/hooks/useQuery';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import TeamAPI from '../../../containers/team/api/TeamAPI';
import { dataIdGenerator } from '../../../dashboard/components/top-bar/Topbar';
import { ContactCard } from '../../interfaces/accountManager';
import {
  ACCOUNT_MANAGER_FALLBACK_EMAIL,
  ACCOUNT_MANAGER_ILLUSTRATION,
  ACCOUNT_MANAGER_ILLUSTRATION_DARK_MODE
} from '../../constants/accountManager';
import { AccountManagerDefaultAvatar } from './AccountManagerDefaultAvatar';
import { ACCOUNT_MANAGER_CONTACT_DIALOG_DATA_IDS } from './data-id';
import { AccountManagerDialogTrackingEvents } from '../../tracking/accountManagerContactDialogTracking';

export function AccountManagerContactDialog({ onClose }: { onClose: () => void }) {
  const { eventTrack } = useAnalyticsTracker();

  const { data, uiState, error, fetch } = useQuery({
    dataSource$: () => {
      return TeamAPI.getAccountManagers();
    }
  });

  const contactCards = (data || []) as ContactCard[];
  const fallbackEmail = ACCOUNT_MANAGER_FALLBACK_EMAIL;

  const supportIllustration = useEntityLogo(
    ACCOUNT_MANAGER_ILLUSTRATION,
    ACCOUNT_MANAGER_ILLUSTRATION_DARK_MODE
  );

  useEffect(() => {
    fetch();
  }, []);

  const dataIds = ACCOUNT_MANAGER_CONTACT_DIALOG_DATA_IDS;

  return (
    <>
      <div className='dialog-header--absolute'>
        <IconButton label='close' dataId={dataIdGenerator('close-btn')} onClick={onClose}>
          <Icon name='close' />
        </IconButton>
      </div>

      <div className='p-5'>
        <div className='text-subheading-2 mb-5'>Contact Account Manager</div>

        {uiState === EntityUIState.LOADING ? (
          <div className='center-flex-row justify-center p-6 py-8'>
            <GenericPageShimmer />
          </div>
        ) : null}

        {uiState === EntityUIState.IDLE && contactCards.length ? (
          <div className='p-6' data-id={dataIds.contactList}>
            {contactCards.map((card, index) => (
              <div
                className={clsx('center-flex-row', index > 0 && 'border-top pt-7 mt-7')}
                key={card.email}
              >
                <div style={{ width: '160px' }} className='d-flex justify-center'>
                  <AccountManagerDefaultAvatar palette={card.palette} />
                </div>

                <div className='flex-1 ml-7'>
                  <div className='text-body-3'>{card.name}</div>

                  <div className='mt-1'>{card.role}</div>

                  <p className='text-secondary mt-2'>{card.description}</p>

                  <a
                    className='center-flex-row text-primary mt-2'
                    href={`mailto:${card.email}`}
                    data-id={dataIds.getContactEmail(card.email)}
                    onClick={() => {
                      eventTrack({
                        action: AccountManagerDialogTrackingEvents.contactEmailClick,
                        properties: {
                          contactRole: card.role
                        }
                      });
                    }}
                    target='_blank'
                  >
                    <Icon name='email' size={1} className='mr-1' />
                    <span>{card.email}</span>
                  </a>

                  <HdButton
                    tag='a'
                    href={`mailto:${card.email}`}
                    target='_blank'
                    icon='email'
                    dataId={dataIds.getContactButton(card.email)}
                    className='mt-5'
                    onClick={() => {
                      eventTrack({
                        action: AccountManagerDialogTrackingEvents.contactButtonClick,
                        properties: {
                          contactRole: card.role
                        }
                      });
                    }}
                  >
                    Contact
                  </HdButton>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {uiState === EntityUIState.ERRORED ||
        (uiState === EntityUIState.IDLE && !contactCards.length) ? (
          <div className='center-flex-row p-6' data-id={dataIds.emptyList}>
            <div style={{ width: '160px' }} className='d-flex justify-center'>
              <img alt='' style={{ width: '100%' }} src={supportIllustration} />
            </div>

            <div className='flex-1 ml-7'>
              <div className='text-body-3' data-id={dataIds.emptyListTitle}>
                {!!error ? (
                  <>Your account manager details aren't available at the moment.</>
                ) : (
                  <>No dedicated manager assigned yet.</>
                )}
              </div>

              <p className='text-secondary mt-1'>
                If you need help with anything, whether it’s setup, pipelines, or billing, just drop
                us a note at{' '}
                <a
                  href={`mailto:${fallbackEmail}`}
                  target='_blank'
                  data-id={dataIds.fallbackEmail}
                  className='text-primary'
                  onClick={() => {
                    eventTrack({
                      action: AccountManagerDialogTrackingEvents.fallbackEmailClick
                    });
                  }}
                >
                  {fallbackEmail}
                </a>
              </p>

              <HdButton
                tag='a'
                dataId={dataIds.fallbackButton}
                href={`mailto:${fallbackEmail}`}
                target='_blank'
                icon='email'
                className='mt-5'
                onClick={() => {
                  eventTrack({
                    action: AccountManagerDialogTrackingEvents.fallbackButtonClick
                  });
                }}
              >
                Contact
              </HdButton>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
